import React, { useEffect, useMemo, useState } from 'react';
import {
  applyBrandColor,
  fetchVenueSettings,
  getCachedVenueSettings,
  type VenueSettings,
} from '../../services/venueService';
import { calcOrderTotals, formatTaxLabel } from '../../config/venueConfig';

interface VenueContextValue {
  settings: VenueSettings;
  loading: boolean;
  refresh: () => Promise<void>;
  taxLabel: string;
  calcTotals: typeof calcOrderTotals;
  sections: string[];
  cuisineTypes: NonNullable<VenueSettings['cuisineTypes']>;
}

const VenueContext = React.createContext<VenueContextValue | null>(null);

export const VenueProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<VenueSettings>(getCachedVenueSettings());
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const next = await fetchVenueSettings();
    setSettings(next);
    applyBrandColor(next.color);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const value = useMemo(
    () => ({
      settings,
      loading,
      refresh: load,
      taxLabel: formatTaxLabel(settings.taxRate),
      calcTotals: (subtotal: number) => calcOrderTotals(subtotal, settings),
      sections: settings.sections?.length ? settings.sections : [],
      cuisineTypes: settings.cuisineTypes?.length ? settings.cuisineTypes : getCachedVenueSettings().cuisineTypes || [],
    }),
    [settings, loading]
  );

  return <VenueContext.Provider value={value}>{children}</VenueContext.Provider>;
};

export function useVenueContext() {
  const ctx = React.useContext(VenueContext);
  if (!ctx) throw new Error('useVenueContext must be used within VenueProvider');
  return ctx;
}
